import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpEventType } from '@angular/common/http';
import { ApiService } from './api.service';
import { FILE_RULES, FilePurpose, IFileConfirmRequest, IFileMedia, IPresign, IPresignRequest } from '../models/file.model';

export interface IPreparedFile {
  blob: Blob;
  name: string;
  mimeType: string;
  width: number;
  height: number;
  previewUrl: string;
  /** Solo card_front: resolución menor a 1800×1030. */
  lowResolution?: boolean;
}

export interface IUploadResult { ok: boolean; file?: IFileMedia; message?: string; }

/**
 * Subida de archivos con el flujo del contrato: POST /files/presign → PUT a S3 → POST /files/confirm.
 * Los logos se ajustan en el navegador al tamaño exacto que pide la API (512×512 y 1200×400).
 */
@Injectable({ providedIn: 'root' })
export class FileUploadService {

  private http = inject(HttpClient);
  private api = inject(ApiService);

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR prepare                                */
  /* -------------------------------------------------------------------------- */
  // region prepare
  async prepare(file: File, purpose: FilePurpose): Promise<{ ok: true; prepared: IPreparedFile } | { ok: false; message: string }> {
    const rules = FILE_RULES[purpose];
    const mime = file.type || (file.name.toLowerCase().endsWith('.svg') ? 'image/svg+xml' : '');
    if (!rules.mimes.includes(mime)) {
      const allowed = rules.mimes.map(m => m.includes('svg') ? 'SVG' : m.includes('jpeg') ? 'JPG' : 'PNG').join(' o ');
      return { ok: false, message: `Usa un archivo ${allowed}.` };
    }

    let dims = { width: 0, height: 0 };
    try { dims = await this.readDimensions(file); } catch { return { ok: false, message: 'No pudimos leer la imagen.' }; }

    if (rules.exact) {
      const [w, h] = rules.exact;
      const ratio = w / h;
      const actual = dims.width / dims.height;
      if (Math.abs(actual - ratio) > 0.03) {
        return { ok: false, message: purpose === 'logo_square' ? 'El logo cuadrado debe ser 1:1.' : 'El logo horizontal debe ser 3:1.' };
      }
      if (dims.width < w || dims.height < h) {
        return { ok: false, message: `Usa una imagen de ${w}×${h} px o más.` };
      }
      const blob = dims.width === w && dims.height === h && mime === 'image/png' ? file : await this.resizePng(file, w, h);
      if (blob.size > rules.maxBytes) return { ok: false, message: `El archivo supera ${rules.maxBytes / 1024 / 1024} MB.` };
      return { ok: true, prepared: { blob, name: file.name.replace(/\.\w+$/, '') + '.png', mimeType: 'image/png', width: w, height: h, previewUrl: URL.createObjectURL(blob) } };
    }

    if (file.size > rules.maxBytes) return { ok: false, message: `${file.name} supera ${rules.maxBytes / 1024 / 1024} MB` };
    const low = !!rules.min && (dims.width < rules.min[0] || dims.height < rules.min[1]);
    return { ok: true, prepared: { blob: file, name: file.name, mimeType: mime, width: dims.width, height: dims.height, previewUrl: URL.createObjectURL(file), lowResolution: low } };
  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR upload                                */
  /* -------------------------------------------------------------------------- */
  // region upload
  async upload(prepared: IPreparedFile, purpose: FilePurpose, idCompany: string, onProgress?: (pct: number) => void): Promise<IUploadResult> {
    const presignBody: IPresignRequest = {
      idCompany, purpose, fileName: prepared.name, mimeType: prepared.mimeType, sizeBytes: prepared.blob.size,
    };
    const presign = await this.api.post<IPresign>('/files/presign', presignBody);
    if (!presign.ok || !presign.data) return { ok: false, message: presign.message };

    const put = await new Promise<boolean>(resolve => {
      this.http.put(presign.data!.presignedUrl, prepared.blob, {
        headers: { 'Content-Type': prepared.mimeType },
        reportProgress: true,
        observe: 'events',
      }).subscribe({
        next: ev => {
          if (ev.type === HttpEventType.UploadProgress && ev.total) onProgress?.(Math.round((ev.loaded / ev.total) * 100));
          if (ev.type === HttpEventType.Response) resolve(true);
        },
        error: () => resolve(false),
      });
    });
    if (!put) return { ok: false, message: 'No pudimos subir el archivo. Intenta de nuevo.' };

    const confirmBody: IFileConfirmRequest = {
      s3Key: presign.data.s3Key, idCompany, purpose, originalName: prepared.name, mimeType: prepared.mimeType,
      sizeBytes: prepared.blob.size, width: prepared.width || null, height: prepared.height || null,
    };
    const confirm = await this.api.post<{ file: IFileMedia }>('/files/confirm', confirmBody);
    if (!confirm.ok || !confirm.data?.file) return { ok: false, message: confirm.message };
    onProgress?.(100);
    return { ok: true, file: confirm.data.file };
  }

  // region readDimensions
  readDimensions(file: Blob): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => { resolve({ width: img.naturalWidth || 512, height: img.naturalHeight || 512 }); URL.revokeObjectURL(url); };
      img.onerror = () => { reject(new Error('image')); URL.revokeObjectURL(url); };
      img.src = url;
    });
  }

  private async resizePng(file: Blob, w: number, h: number): Promise<Blob> {
    const url = URL.createObjectURL(file);
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = url;
      });
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d')!;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, w, h);
      return await new Promise<Blob>(resolve => canvas.toBlob(b => resolve(b!), 'image/png'));
    } finally {
      URL.revokeObjectURL(url);
    }
  }
}
